import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { saveTask, renameTask, setExerciseCount, createFromTemplate } from '../../redux/slices/tasksSlice';
import { EXERCISE_GROUPS, MIN_COUNT, MAX_COUNT } from './exerciseTypes';

const sumCounts = (exercises, types) =>
    types.reduce((sum, t) => sum + (exercises[t.code] || 0), 0);

// Склонение: 1 пример, 2 примера, 5 примеров
const pluralExamples = (n) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'пример';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'примера';
    return 'примеров';
};

function ExerciseRow({ type, count, disabled, onChange }) {
    const handleInput = (e) => {
        const raw = e.target.value;
        if (raw === '') return onChange(null);
        const n = parseInt(raw, 10);
        if (Number.isNaN(n)) return;
        onChange(n);
    };

    return (
        <li className={'exercise-row' + (count ? ' is-selected' : '') + (disabled ? ' is-readonly' : '')}>
            <span className="exercise-row__number">{type.number}</span>
            <div className="exercise-row__text">
                <span className="exercise-row__label">{type.label}</span>
                <span className="exercise-row__example">например: {type.example}</span>
            </div>
            <input
                className="exercise-row__count"
                type="number"
                inputMode="numeric"
                min={MIN_COUNT}
                max={MAX_COUNT}
                placeholder="—"
                value={count ?? ''}
                onChange={handleInput}
                disabled={disabled}
                aria-label={`Количество: ${type.label}`}
            />
        </li>
    );
}

export default function TaskEditor({ task }) {
    const dispatch = useAppDispatch();
    const savingKey = useAppSelector((state) => state.tasks.savingKey);
    const saveError = useAppSelector((state) => state.tasks.saveError);

    const isSaved = task.id != null;
    const isSaving = savingKey === task.key;
    const exercises = task.exercises || {};
    const total = EXERCISE_GROUPS.reduce((sum, g) => sum + sumCounts(exercises, g.types), 0);
    const hasTitle = task.title.trim().length > 0;
    const canSave = task.isDirty && !isSaving && hasTitle && (isSaved || total > 0);

    const setCount = (type, count) => dispatch(setExerciseCount({ key: task.key, type, count }));

    return (
        <div className="task-editor">
            <div className="task-editor__header">
                <input
                    className="task-editor__name"
                    value={task.title}
                    onChange={(e) => dispatch(renameTask({ key: task.key, title: e.target.value }))}
                    placeholder="Название задания"
                />
                <button
                    className="task-btn task-btn--primary"
                    disabled={!canSave}
                    onClick={() => dispatch(saveTask(task.key))}
                >
                    {isSaving ? 'Сохранение…' : 'Сохранить задание'}
                </button>
                <button
                    className="task-btn task-btn--secondary"
                    disabled={!isSaved}
                    onClick={() => dispatch(createFromTemplate(task.key))}
                    title={isSaved ? 'Создать новое задание с таким же составом' : 'Сначала сохраните задание'}
                >
                    Использовать как шаблон
                </button>
            </div>

            {saveError && <p className="task-error">{saveError}</p>}
            {!hasTitle && <p className="task-error">Название не может быть пустым</p>}

            {isSaved ? (
                <p className="task-hint">
                    Задание сохранено — его состав изменить нельзя, можно только переименовать.
                    Чтобы сделать похожее, нажмите «Использовать как шаблон».
                </p>
            ) : (
                <p className="task-hint">
                    Новое задание, ещё не сохранено. Укажите, сколько примеров каждого типа в него войдёт
                    (от {MIN_COUNT} до {MAX_COUNT}). Пустое поле — тип не используется.
                </p>
            )}

            <div className="exercise-groups">
                {EXERCISE_GROUPS.map((group) => (
                    <section key={group.id} className="exercise-group">
                        <h3 className="exercise-group__title">
                            {group.title}
                            <span className="exercise-group__sum">{sumCounts(exercises, group.types)}</span>
                        </h3>
                        <ul className="exercise-list">
                            {group.types.map((type) => (
                                <ExerciseRow
                                    key={type.code}
                                    type={type}
                                    count={exercises[type.code]}
                                    disabled={isSaved}
                                    onChange={(count) => setCount(type.code, count)}
                                />
                            ))}
                        </ul>
                    </section>
                ))}
            </div>

            <div className="task-editor__total">
                {total > 0
                    ? <>Итого в задании: <b>{total}</b> {pluralExamples(total)}</>
                    : <span className="task-hint">Выберите хотя бы один тип примеров, чтобы сохранить задание</span>}
            </div>
        </div>
    );
}
